import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HorizontalScrollComponent,
  NoItemComponent,
  PageLayoutComponent,
  SearchComponent,
  TitleSummaryComponent,
} from '@client-monorepo/common/ui-components';
import { getHostname, rangeCreator, ScrolledToEndDirective } from '@client-monorepo/common/utilities';
import { SuggestedSearchesComponent } from '../../components/suggested-searches/suggested-searches.component';
import {
  ProductApiService,
  ProductInterface,
  ProductListPayloadInterface,
  ProductPreviewComponent,
  ProductsSortAndFilterComponent,
  ProductsSortAndFilterService,
  Store,
  StorePreviewComponent,
  StoreRestrictionFields,
  StoresApiService,
} from '@client-monorepo/daily-fintech/stores';
import { PopularStoresComponent } from '../../components/popular-stores/popular-stores.component';
import { CategoriesMiniViewComponent } from '../../components/categories-mini-view/categories-mini-view.component';
import { Subscription, zip } from 'rxjs';
import { DpIconComponent } from '@client-monorepo/common/icon';
import { Router } from '@angular/router';
import { Order, OrderTypes, Restriction, RestrictionTypes, SearchPayloadInterface } from '@client-monorepo/common/network';
import { NgxBottomNavigationService } from '@digipay/ngx-bottom-navigation';
import { AnalyticsEventType, EventManagementService } from '@client-monorepo/common/event-management';
import { ActionHandlerService, ActionType, RedirectionTypeEnum } from '@client-monorepo/common/action-handler';
import { toObservable } from '@angular/core/rxjs-interop';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { SearchHistoryComponent } from '../../components/search-history/search-history.component';
import { SearchHistoryService } from '../../data-access/services/search-history.service';

@Component({
  selector: 'stores-applet-stores-search',
  standalone: true,
  imports: [
    CommonModule,
    NoItemComponent,
    SearchComponent,
    SuggestedSearchesComponent,
    StorePreviewComponent,
    PopularStoresComponent,
    CategoriesMiniViewComponent,
    DpIconComponent,
    TitleSummaryComponent,
    HorizontalScrollComponent,
    ProductPreviewComponent,
    PageLayoutComponent,
    ScrolledToEndDirective,
    ProductsSortAndFilterComponent,
    SearchHistoryComponent,
  ],
  templateUrl: './stores-search.component.html',
  styleUrl: './stores-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoresSearchComponent implements OnInit, OnDestroy, AfterViewChecked {
  showingStores = signal<Array<Store>>([]);
  showingSuggestions = signal<Array<string>>([]);
  totalStores = signal<Array<Store>>([]);
  totalProducts = signal<Array<ProductInterface>>([]);
  searchText = signal<string>('');
  searching = signal<boolean>(false);
  totalSearching = signal<boolean>(false);
  searched = signal<boolean>(false);
  totalSearched = signal<boolean>(false);
  ctaSearchText = computed(() => {
    return `جستجوی "${this.searchText()}"`;
  });
  storeApiService = inject(StoresApiService);
  productApiService = inject(ProductApiService);
  backHandlerService = inject(BackHandlerService);
  rangeCreator = rangeCreator;
  searchSubscription!: Subscription;
  totalSearchSubscription!: Subscription;
  queryId = signal<string>('');
  router = inject(Router);
  searchComponent = viewChild<SearchComponent>('searchComponent');
  initialized = signal(false);
  bottomNavigationService = inject(NgxBottomNavigationService);
  actionHandler = inject(ActionHandlerService);
  paginationInitialized = signal(false);
  page = 0;
  perPage = 30;
  hasMorePage = false;
  searchTarget = '';
  eventManagement = inject(EventManagementService);
  sort = computed(() => {
    return this.sortAndFilterService.data().sort;
  });
  filters = computed(() => {
    return this.sortAndFilterService.data().filters;
  });
  sortAndFilterService = inject(ProductsSortAndFilterService);
  isPriceFilterModified = computed(() => {
    return this.sortAndFilterService.isPriceFilterModified();
  });
  hasHistory = signal<boolean>(true);
  searchHistoryService = inject(SearchHistoryService);
  subscriptions: Subscription[] = [];

  constructor() {
    this.subscriptions.push(
      toObservable(this.sort).subscribe({
        next: () => {
          this.sortChange();
        },
      }),
    );
    this.subscriptions.push(
      toObservable(this.filters).subscribe({
        next: () => {
          this.filtersChange();
        },
      }),
    );
  }
  ngOnInit() {
    this.searchHistoryService.refreshSearchHistory();
    this.bottomNavigationService.hide();
    this.backHandlerService.setCustomBackUrl('/stores');
    this.resetSortAndFilter();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.totalSearchSubscription) {
      this.totalSearchSubscription.unsubscribe();
    }
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
    this.bottomNavigationService.show();
  }
  ngAfterViewChecked() {
    if (this.searchComponent()?.searchEl() && !this.initialized()) {
      setTimeout(() => {
        this.initialized.set(true);
        this.searchComponent()?.searchEl()?.nativeElement.focus();
      });
    }
  }

  suggestionClick(suggestion: string): void {
    this.searchText.set(suggestion);
    this.searchEnd(suggestion);
  }

  doSearch(searchText = ''): void {
    if (searchText && searchText.length > 2) {
      this.page = 0;
      this.searchTarget = '';
      this.paginationInitialized.set(false);
      this.searching.set(true);
      this.totalSearching.set(false);
      this.totalSearched.set(false);
      this.totalProducts.set([]);
      this.resetSortAndFilter();
      const payload: SearchPayloadInterface<StoreRestrictionFields> = {
        restrictions: [
          {
            type: RestrictionTypes.SIMPLE,
            field: StoreRestrictionFields.KEYWORD,
            value: searchText,
            operation: 'eq',
          },
        ],
        orders: [
          {
            order: OrderTypes.ASC,
            field: 'priority',
          },
        ],
        size: 10,
        page: 1,
      };
      if (this.searchSubscription) {
        this.searchSubscription.unsubscribe();
      }
      if (this.totalSearchSubscription) {
        this.totalSearchSubscription.unsubscribe();
      }
      this.searchSubscription = zip([
        this.storeApiService.getAllStores(payload),
        this.productApiService.getSearchSuggestions(searchText),
      ]).subscribe({
        next: ([storesRes, suggestionsRes]) => {
          this.showingStores.set(storesRes);
          this.showingSuggestions.set(suggestionsRes.topQuerySuggestions);
          this.searching.set(false);
          this.searched.set(true);
        },
        error: () => {
          this.showingStores.set([]);
          this.showingSuggestions.set([]);
          this.searching.set(false);
          this.searched.set(true);
        },
        complete: () => {
          this.searchSubscription.unsubscribe();
        },
      });
    } else {
      this.showingStores.set([]);
      this.searching.set(false);
      this.searched.set(false);
      this.totalSearched.set(false);
      this.page = 0;
      this.searchTarget = '';
      this.paginationInitialized.set(false);
      this.totalProducts.set([]);
      this.sortAndFilterService.resetSortAndFilter();
    }
  }

  searchEnd(searchText: string): void {
    if (searchText && searchText.length > 2) {
      const storesPayload: SearchPayloadInterface<StoreRestrictionFields> = {
        restrictions: [
          {
            type: RestrictionTypes.SIMPLE,
            field: StoreRestrictionFields.KEYWORD,
            value: searchText,
            operation: 'eq',
          },
        ],
        orders: [
          {
            order: OrderTypes.ASC,
            field: 'priority',
          },
        ],
      };
      const productsPayload: ProductListPayloadInterface = {
        keyword: searchText,
        available: true,
        restrictions: this.filters() as Restriction<string>[],
        orders: this.sort()?.field ? ([this.sort()] as Order[]) : [],
        size: this.perPage,
        page: this.page,
      };
      if (this.searchSubscription) {
        this.searchSubscription.unsubscribe();
      }
      if (this.totalSearchSubscription) {
        this.totalSearchSubscription.unsubscribe();
      }
      this.totalSearching.set(true);
      this.searched.set(false);
      this.searching.set(false);
      this.totalSearchSubscription = zip([
        this.storeApiService.getAllStores(storesPayload),
        this.productApiService.getAllProducts(productsPayload),
      ]).subscribe({
        next: ([storesRes, productsRes]) => {
          this.totalStores.set(storesRes);
          this.totalProducts.update((oldProducts) => {
            return oldProducts.concat(productsRes.products);
          });
          if (this.totalProducts().length && this.page === 0) {
            this.searchHistoryService.pushToSearchHistory(searchText);
          }
          this.queryId.set(productsRes.queryId);
          this.totalSearching.set(false);
          this.totalSearched.set(true);
          this.searchTarget = searchText;
          this.paginationInitialized.set(true);
          this.hasMorePage = productsRes.products.length >= this.perPage;
        },
        complete: () => {
          this.totalSearchSubscription.unsubscribe();
        },
      });
    } else {
      this.totalSearching.set(false);
      this.totalSearched.set(false);
      this.searched.set(false);
      this.searching.set(false);
      this.sortAndFilterService.resetSortAndFilter();
    }
  }

  handleProductClick(product: ProductInterface): void {
    this.eventManagement.triggerEventImmediately({
      type: AnalyticsEventType.REDIRECT,
      to: product.url,
      meta: {
        host: getHostname(product.url),
      },
      breadCrumbs: ['stores-search', 'product'],
    });
    this.actionHandler.handle({
      type: ActionType.REDIRECT,
      payload: {
        type: RedirectionTypeEnum.blank,
        url: product.url,
        params: {
          'dp-source': 'DP',
          'dp-medium': 'search-page',
          'dp-type': 'product',
        },
      },
    });
  }

  handleStoreClick(store: Store) {
    this.router.navigate([`/stores/${store.title}`]);
  }

  handleNewPage(): void {
    if (this.paginationInitialized() && this.hasMorePage) {
      this.page++;
      this.searchEnd(this.searchTarget);
    }
  }

  sortChange(): void {
    this.page = 0;
    this.hasMorePage = true;
    this.totalProducts.set([]);
    this.searchEnd(this.searchTarget);
  }

  filtersChange(): void {
    this.page = 0;
    this.hasMorePage = true;
    this.totalProducts.set([]);
    this.searchEnd(this.searchTarget);
  }

  resetSortAndFilter(): void {
    this.sortAndFilterService.resetSortAndFilter();
  }

  handleHasHistory(hasHistory: boolean): void {
    this.hasHistory.set(hasHistory);
  }
}
